import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { hasAnyExistingMetrics } from '../lib/metricsExistence'
import {
  calculateAllMetrics,
  validateMetrics
} from '../utils/financialCalculations'
import { addDays, formatDateToYMD, startOfDay } from '../utils/metricDateUtils'
import { applyWeeklyMetricsAggregatesUpdate } from '../utils/monthlyRollup'
import CalculatedMetricsPreview from './CalculatedMetricsPreview'

const WeeklyMetricsForm = ({ selectedWeekStart, onSave, existingData, onDataChange, onFirstMetricSaved }) => {
  const { user } = useAuth()
  const { t, i18n } = useTranslation(['dashboard', 'common'])

  const [formData, setFormData] = useState({
    revenue: '',
    expenses: '',
    leads: '',
    customers: '',
    // social_following: ''
  })
  const [calculatedMetrics, setCalculatedMetrics] = useState({})
  const [previousMetrics, setPreviousMetrics] = useState(null)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const customersExceedLeads = formData.customers !== '' && Number(formData.customers) > (Number(formData.leads) || 0)
  const customerError = customersExceedLeads
    ? t('dataEntry.validation.customersExceedLeads')
    : errors.customers === 'Customers cannot exceed leads' ? '' : errors.customers

  useEffect(() => {
    if (existingData) {
      setFormData({
        revenue: existingData.revenue?.toString() || '',
        expenses: existingData.expenses?.toString() || '',
        leads: existingData.leads?.toString() || '',
        customers: existingData.customers?.toString() || '',
        // social_following: existingData.social_following?.toString() || ''
      })
    } else {
      setFormData({
        revenue: '',
        expenses: '',
        leads: '',
        customers: '',
        // social_following: ''
      })
    }
  }, [existingData, selectedWeekStart])

  useEffect(() => {
    fetchPreviousWeekData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeekStart])

  useEffect(() => {
    const numericData = {
      revenue: parseFloat(formData.revenue) || 0,
      expenses: parseFloat(formData.expenses) || 0,
      leads: parseInt(formData.leads) || 0,
      customers: parseInt(formData.customers) || 0
    }

    const metrics = calculateAllMetrics(numericData, previousMetrics)
    setCalculatedMetrics(metrics)
  }, [formData, previousMetrics])


  const fetchPreviousWeekData = async () => {
    if (!user || !selectedWeekStart) return

    try {
      const prevWeekStart = addDays(startOfDay(selectedWeekStart), -7)

      const { data: business } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (!business) return

      const { data } = await supabase
        .from('weekly_metrics')
        .select('*')
        .eq('business_id', business.id)
        .eq('week_start_date', formatDateToYMD(prevWeekStart))
        .single()

      setPreviousMetrics(data)
    } catch (error) {
      // No previous data
      setPreviousMetrics(null)
    }
  }

  const formatWeekDisplay = (weekStart) => {
    const start = startOfDay(weekStart)
    const end = addDays(start, 6)

    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US'
    const startLabel = start.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric'
    })

    const endLabel = end.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })

    return `${startLabel} – ${endLabel}`
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const numericData = {
      revenue: parseFloat(formData.revenue) || 0,
      expenses: parseFloat(formData.expenses) || 0,
      leads: parseInt(formData.leads) || 0,
      customers: parseInt(formData.customers) || 0,
      // social_following: parseInt(formData.social_following) || 0
    }

    const validation = validateMetrics(numericData)
    if (!validation.isValid) {
      setErrors(validation.errors)
      return
    }

    setSaving(true)
    setErrors({})

    try {
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (businessError) throw businessError
      const hadAnyMetricsBeforeSave = await hasAnyExistingMetrics(business.id)

      const metricsData = {
        business_id: business.id,
        week_start_date: formatDateToYMD(startOfDay(selectedWeekStart)),
        ...numericData
      }

      const { data: existingRecord } = await supabase
        .from('weekly_metrics')
        .select('id')
        .eq('business_id', business.id)
        .eq('week_start_date', metricsData.week_start_date)
        .single()

      let result
      if (existingRecord) {
        result = await supabase
          .from('weekly_metrics')
          .update(metricsData)
          .eq('id', existingRecord.id)
      } else {
        result = await supabase.from('weekly_metrics').insert(metricsData)
      }

      if (result.error) throw result.error

      // Keep monthly aggregates in sync (Monthly Performance Overview uses monthly_metrics).
      try {
        await applyWeeklyMetricsAggregatesUpdate({
          businessId: business.id,
          weekStartDate: selectedWeekStart,
          previous: existingData,
          next: metricsData,
        })
      } catch (aggregateError) {
        // Non-blocking: weekly entry succeeded; aggregates can be recomputed later if needed.
        console.error('Weekly aggregates update failed:', aggregateError)
      }

      if (onSave) onSave(metricsData)
      if (onDataChange) await onDataChange()

      if (!hadAnyMetricsBeforeSave && onFirstMetricSaved) {
        onFirstMetricSaved()
        return
      }

      setSuccessMessage(t('dataEntry.saveSuccess'))
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error) {
      console.error('Error saving weekly metrics:', error)
      setErrors({ general: t('dataEntry.saveError') })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-w-0 bg-white border border-gray-200 border-t-[3px] border-t-[#FF1B54] shadow-sm rounded-2xl p-6 sm:p-8">
      <div className="mb-7">
        <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 mb-2">{t('dataEntry.weeklyTitle')}</h3>
        <p className="text-base leading-6 text-gray-600">
          {t('dataEntry.weeklySubtitle', { week: formatWeekDisplay(selectedWeekStart) })}
        </p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
          {errors.general}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          <div>
            <label htmlFor="revenue" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.revenue')} ($)
            </label>
            <input
              type="number"
              id="revenue"
              name="revenue"
              value={formData.revenue}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                errors.revenue ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="10000.00"
            />
            {errors.revenue && <p className="mt-1 text-sm text-red-600">{errors.revenue}</p>}
          </div>

          <div>
            <label htmlFor="expenses" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.expenses')} ($)
            </label>
            <input
              type="number"
              id="expenses"
              name="expenses"
              value={formData.expenses}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                errors.expenses ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="7500.00"
            />
            {errors.expenses && <p className="mt-1 text-sm text-red-600">{errors.expenses}</p>}
          </div>

          <div>
            <label htmlFor="leads" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.leads')}
            </label>
            <input
              type="number"
              id="leads"
              name="leads"
              value={formData.leads}
              onChange={handleInputChange}
              min="0"
              step="1"
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                errors.leads ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="150"
            />
            {errors.leads && <p className="mt-1 text-sm text-red-600">{errors.leads}</p>}
          </div>

          <div>
            <label htmlFor="customers" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.customersAcquired')}
            </label>
            <input
              type="number"
              id="customers"
              name="customers"
              value={formData.customers}
              onChange={handleInputChange}
              min="0"
              step="1"
              max={formData.leads === '' ? 0 : Number(formData.leads)}
              aria-invalid={Boolean(customerError)}
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                customerError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="25"
            />
            {customerError && <p className="mt-1 text-sm text-red-600">{customerError}</p>}
          </div>

          {/* Social Following (temporarily removed) */}
          {/**
           * TODO: Re-enable social_following when we decide how/where it should be used.
           */}
          {/*
          <div className="md:col-span-2">
            <label htmlFor="social_following" className="block text-sm font-medium text-gray-700">
              Social Media Following
            </label>
            <input
              type="number"
              id="social_following"
              name="social_following"
              value={formData.social_following}
              onChange={handleInputChange}
              min="0"
              step="1"
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                errors.social_following ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="1250"
            />
            {errors.social_following && (
              <p className="mt-1 text-sm text-red-600">{errors.social_following}</p>
            )}
          </div>
          */}
        </div>

        {(formData.revenue || formData.expenses) && (
          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">{t('dataEntry.calculatedPreview')}</h4>
            <CalculatedMetricsPreview metrics={calculatedMetrics} previousMetrics={previousMetrics} />
            <p className="text-sm text-gray-600 text-center mt-8">
              {t('dataEntry.viewDashboardHint')}
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || customersExceedLeads}
            className="h-12 bg-[#FF1B54] hover:bg-[#E1174C] text-white px-7 rounded-lg font-semibold shadow-sm hover:shadow transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {saving ? t('dataEntry.saving') : t('dataEntry.saveMetrics')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default WeeklyMetricsForm
